/**
 * Datacrate strategic partner overlays — merged onto directory entries at seed time.
 */
import type { Supplier } from '../types/supplyChain';

const NOW = '2026-05-22T00:00:00.000Z';

export const STRATEGIC_PARTNER_OVERLAYS: readonly (Partial<Supplier> & { id: string })[] = [
  {
    id: 'supplier-evapco',
    name: 'Evapco',
    categories: ['cooling'],
    hq: { country: 'US', city: 'Taneytown, MD' },
    reliabilityTier: 'tier-1',
    typicalLeadMonths: { low: 8, high: 16 },
    capacity: 'Evaporative cooling towers, closed-circuit coolers — BTM/BOP',
    partnership: { status: 'strategic-agreement', agreementLabel: 'Datacrate framework supply' },
    contacts: [{ role: 'sales', name: 'Sales', email: 'sales@evapco.com' }],
    dataFreshness: { lastUpdatedAt: NOW, source: 'strategic-seed' },
  },
  {
    id: 'supplier-solar-turbines',
    partnership: { status: 'strategic-agreement', agreementLabel: 'Datacrate preferred OEM' },
    contacts: [{ role: 'sales', email: 'info@solarturbines.com' }],
    dataFreshness: { lastUpdatedAt: NOW, source: 'strategic-seed' },
  },
  {
    id: 'supplier-caterpillar-power',
    partnership: { status: 'strategic-agreement', agreementLabel: 'Datacrate preferred OEM' },
    contacts: [{ role: 'sales', email: 'electricpower@caterpillar.com' }],
    dataFreshness: { lastUpdatedAt: NOW, source: 'strategic-seed' },
  },
  {
    id: 'supplier-areva-framatome',
    partnership: { status: 'strategic-agreement', agreementLabel: 'Datacrate nuclear supply framework' },
    contacts: [{ role: 'sales', email: 'contact@framatome.com' }],
    dataFreshness: { lastUpdatedAt: NOW, source: 'strategic-seed' },
  },
  {
    id: 'supplier-arc-clean-tech',
    name: 'ARC Clean Technology',
    categories: ['power-generation'],
    hq: { country: 'CA', city: 'Saint John, NB' },
    reliabilityTier: 'tier-2',
    typicalLeadMonths: { low: 36, high: 60 },
    partnership: { status: 'strategic-agreement', agreementLabel: 'Datacrate SMR collaboration' },
    contacts: [{ role: 'sales', email: 'info@arcenergy.co' }],
    dataFreshness: { lastUpdatedAt: NOW, source: 'strategic-seed' },
  },
  {
    id: 'supplier-deep-fission',
    name: 'Deep Fission',
    categories: ['power-generation'],
    hq: { country: 'US', city: 'Berkeley, CA' },
    reliabilityTier: 'tier-2',
    typicalLeadMonths: { low: 48, high: 72 },
    partnership: { status: 'loi', agreementLabel: 'LOI — modular nuclear supply' },
    contacts: [{ role: 'sales', email: 'contact@deepfission.com' }],
    dataFreshness: { lastUpdatedAt: NOW, source: 'strategic-seed' },
  },
  {
    id: 'supplier-paragon-energy',
    name: 'Paragon Energy Solutions',
    categories: ['power-conversion'],
    hq: { country: 'US', city: 'Houston, TX' },
    reliabilityTier: 'tier-2',
    typicalLeadMonths: { low: 12, high: 24 },
    partnership: { status: 'strategic-agreement' },
    contacts: [{ role: 'sales', email: 'info@paragonenergy.com' }],
    dataFreshness: { lastUpdatedAt: NOW, source: 'strategic-seed' },
  },
  {
    id: 'supplier-ls-electric',
    name: 'LS Electric',
    categories: ['power-conversion'],
    hq: { country: 'KR', city: 'Anyang' },
    reliabilityTier: 'tier-1',
    typicalLeadMonths: { low: 10, high: 20 },
    partnership: { status: 'strategic-agreement' },
    contacts: [{ role: 'sales', email: 'global@lselectric.co.kr' }],
    dataFreshness: { lastUpdatedAt: NOW, source: 'strategic-seed' },
  },
  {
    id: 'supplier-gleeds',
    name: 'Gleeds',
    categories: ['commissioning', 'civil-construction'],
    hq: { country: 'GB', city: 'London' },
    reliabilityTier: 'tier-1',
    partnership: { status: 'mou', agreementLabel: 'Consultant MOU' },
    contacts: [{ role: 'general', email: 'info@gleeds.com' }],
    dataFreshness: { lastUpdatedAt: NOW, source: 'strategic-seed' },
  },
  {
    id: 'supplier-red-engineering',
    name: 'Red Engineering',
    categories: ['commissioning', 'data-center-it'],
    hq: { country: 'GB', city: 'London' },
    reliabilityTier: 'tier-1',
    partnership: { status: 'mou', agreementLabel: 'Consultant MOU' },
    contacts: [{ role: 'general', email: 'info@red-engineering.com' }],
    dataFreshness: { lastUpdatedAt: NOW, source: 'strategic-seed' },
  },
];

export function mergeStrategicPartnerOverlay(base: Supplier): Supplier {
  const overlay = STRATEGIC_PARTNER_OVERLAYS.find(o => o.id === base.id);
  if (!overlay) return base;
  return {
    ...base,
    ...overlay,
    id: base.id,
    name: overlay.name ?? base.name,
    categories: overlay.categories ?? base.categories,
    hq: { ...base.hq, ...overlay.hq },
    contacts: overlay.contacts ?? base.contacts,
    partnership: overlay.partnership ?? base.partnership,
    dataFreshness: overlay.dataFreshness ?? base.dataFreshness,
  };
}

export function strategicPartnerSuppliersForSeed(baseline: readonly Supplier[]): Supplier[] {
  const byId = new Map(baseline.map(s => [s.id, s]));
  const out: Supplier[] = [];
  for (const overlay of STRATEGIC_PARTNER_OVERLAYS) {
    const base = byId.get(overlay.id);
    if (base) {
      out.push(mergeStrategicPartnerOverlay(base));
    } else if (overlay.name && overlay.categories && overlay.hq) {
      out.push({
        id: overlay.id,
        name: overlay.name,
        categories: overlay.categories,
        hq: overlay.hq,
        reliabilityTier: overlay.reliabilityTier ?? 'tier-2',
        ...(overlay.typicalLeadMonths ? { typicalLeadMonths: overlay.typicalLeadMonths } : {}),
        ...(overlay.capacity ? { capacity: overlay.capacity } : {}),
        ...(overlay.partnership ? { partnership: overlay.partnership } : {}),
        ...(overlay.contacts ? { contacts: overlay.contacts } : {}),
        ...(overlay.dataFreshness ? { dataFreshness: overlay.dataFreshness } : {}),
      });
    }
  }
  return out;
}
